import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import variantImagesData from '../../data/variantImages.json';
import Title from '../../components/Title/Title';
import './VariantDetail.css';
import { useLanguage, Language } from '../../context/LanguageContext';
import { useRegion } from '../../context/RegionContext';
import { getContactModalTranslations, ContactModalTranslations } from '../../translations/contactModal';
import ContactModalBase from '../../components/contactModal/ContactModalBase';
import { useEditMode } from '../../context/EditModeContext';
import { PricingService, PricingResponse } from '../../services/pricingService';

interface WatchVariant {
    id: string;
    imageSrc: string;
    model: string;
    code: string;
    movement: {
        [key: string]: string;  // Multilingual structure
    };
    description: {
        [key: string]: string;  // This allows indexing with language
    };
    subtitle?: string;
    serialNumber?: string;
    case: {
        [key: string]: string;  // Multilingual structure
    };
    dial: {
        [key: string]: string;  // Multilingual structure
    };
    indexGlass: {
        [key: string]: string;  // Multilingual structure
    };
    bracelet: {
        [key: string]: string;  // Multilingual structure
    };
    gender: 'gents' | 'ladies';
    prices?: {
        automatic_movement?: number | string; // Allow string for 'on request'
        quartz_movement?: number | string;    // Allow string for 'on request'
    };
    carousel_images?: string[]; // Added for carousel feature
}

interface TabProps {
    label: string;
    isActive: boolean;
    onClick: () => void;
}

interface VariantImage {
    src: string;
    view: string;
}

interface VariantImagesMap {
    [key: string]: {
        front?: string;
        angle?: string;
        side?: string;
        back?: string;
    };
}

interface AppointmentPopupProps {
    isOpen: boolean;
    onClose: () => void;
}

interface VariantDetailTranslations {
    bookAppointment: string;
    visitStore: string;
    visitStoreDesc: string;
    findStore: string;
    callUs: string;
    callUsDesc: string;
    priceOnRequest: string;
    bookAppointmentButton: string;
    variantNotFound: string;
    serialNumber: string;
    case: string;
    dial: string;
    indexGlass: string;
    bracelet: string;
    material: string;
    movement: string;
    frontView?: string;
    angleView?: string;
    sideView?: string;
    backView?: string;
    gents: string;
    ladies: string;
    automaticMovement: string;
    quartzMovement: string;
}

const variantDetailTranslations: Partial<Record<Language, VariantDetailTranslations>> = {
    en: {
        bookAppointment: "Book Your Appointment",
        visitStore: "Visit a Store",
        visitStoreDesc: "Experience our collection in person at our nearest boutique",
        findStore: "Find Nearest Store",
        callUs: "Call Us",
        callUsDesc: "Speak with our customer service representative",
        priceOnRequest: "PRICE ON REQUEST",
        bookAppointmentButton: "Book Your Appointment",
        variantNotFound: "Variant not found",
        serialNumber: "Serial number",
        case: "CASE",
        dial: "DIAL",
        indexGlass: "INDEX GLASS",
        bracelet: "BRACELET",
        material: "MATERIAL",
        movement: "MOVEMENT",
        frontView: "Front View",
        angleView: "Angle View",
        sideView: "Side View",
        backView: "Back View",
        gents: "GENTS",
        ladies: "LADIES",
        automaticMovement: "Automatic Movement",
        quartzMovement: "Quartz Movement"
    },
    de: {
        bookAppointment: "Termin Vereinbaren",
        visitStore: "Besuchen Sie eine Boutique",
        visitStoreDesc: "Erleben Sie unsere Kollektion persönlich in unserer nächstgelegenen Boutique",
        findStore: "Nächste Boutique Finden",
        callUs: "Rufen Sie Uns An",
        callUsDesc: "Sprechen Sie mit unserem Kundenservice",
        priceOnRequest: "PREIS AUF ANFRAGE",
        bookAppointmentButton: "Termin Vereinbaren",
        variantNotFound: "Variante nicht gefunden",
        serialNumber: "Seriennummer",
        case: "GEHÄUSE",
        dial: "ZIFFERBLATT",
        indexGlass: "INDEXGLAS",
        bracelet: "ARMBAND",
        material: "MATERIAL",
        movement: "UHRWERK",
        frontView: "Vorderansicht",
        angleView: "Winkelansicht",
        sideView: "Seitenansicht",
        backView: "Rückansicht",
        gents: "HERREN",
        ladies: "DAMEN",
        automaticMovement: "Automatikwerk",
        quartzMovement: "Quarzwerk"
    },
    zh: {
        bookAppointment: "預約您的參觀",
        visitStore: "參觀門店",
        visitStoreDesc: "親臨我們最近的精品店，親身體驗我們的系列",
        findStore: "尋找最近的門店",
        callUs: "聯繫我們",
        callUsDesc: "與我們的客戶服務代表交談",
        priceOnRequest: "價格請詢問",
        bookAppointmentButton: "預約您的參觀",
        variantNotFound: "未找到該款式",
        serialNumber: "序列號",
        case: "錶殼",
        dial: "錶盤",
        indexGlass: "刻度玻璃",
        bracelet: "錶帶",
        material: "材質",
        movement: "機芯",
        frontView: "正面視圖",
        angleView: "角度視圖",
        sideView: "側面視圖",
        backView: "背面視圖",
        gents: "男士",
        ladies: "女士",
        automaticMovement: "自動機芯",
        quartzMovement: "石英機芯"
    },
    fr: {
        bookAppointment: "Réservez Votre Rendez-vous",
        visitStore: "Visitez une Boutique",
        visitStoreDesc: "Découvrez notre collection en personne dans notre boutique la plus proche",
        findStore: "Trouver la Boutique la Plus Proche",
        callUs: "Appelez-nous",
        callUsDesc: "Parlez avec notre représentant du service client",
        priceOnRequest: "PRIX SUR DEMANDE",
        bookAppointmentButton: "Réservez Votre Rendez-vous",
        variantNotFound: "Variante non trouvée",
        serialNumber: "Numéro de série",
        case: "BOÎTIER",
        dial: "CADRAN",
        indexGlass: "VERRE À INDEX",
        bracelet: "BRACELET",
        material: "MATÉRIAUX",
        movement: "MOUVEMENT",
        frontView: "Vue de Face",
        angleView: "Vue d'Angle",
        sideView: "Vue de Côté",
        backView: "Vue de Dos",
        gents: "HOMMES",
        ladies: "FEMMES",
        automaticMovement: "Mouvement automatique",
        quartzMovement: "Mouvement à quartz"
    }
};

function getVariantDetailTranslations(language: Language): VariantDetailTranslations {
    return variantDetailTranslations[language] || variantDetailTranslations.en!;
}

const AppointmentPopup: React.FC<AppointmentPopupProps> = ({ isOpen, onClose }) => {
    const { language } = useLanguage();
    const translations = getContactModalTranslations(language);

    return (
        <ContactModalBase
            isOpen={isOpen}
            onClose={onClose}
            translations={translations}
            phoneNumber="+(972) 58-639-8333"
        />
    );
};

const Tab: React.FC<TabProps> = ({ label, isActive, onClick }) => (
    <button
        onClick={onClick}
        className={`relative uppercase text-sm tracking-wider px-4 py-2 ${isActive ? 'font-medium text-black' : 'text-gray-500'
            }`}
    >
        {label}
        {isActive && (
            <div className="absolute bottom-0 left-0 w-full h-[2px] bg-black" />
        )}
    </button>
);

// Watch API client utilities
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const ENABLE_API = process.env.REACT_APP_ENABLE_API === 'true';
const REQUEST_TIMEOUT = 5000;
const timeoutPromise = (timeout: number) => new Promise((_, reject) => setTimeout(() => reject(new Error(`Request timed out after ${timeout}ms`)), timeout));

async function fetchWatch(variantId: string): Promise<WatchVariant> {
    if (!ENABLE_API) {
        const mod = await import(`../../data/watches/${variantId}.json`);
        return mod.default || mod;
    }
    const response = await Promise.race([
        fetch(`${API_BASE_URL}/watches/${variantId}`),
        timeoutPromise(REQUEST_TIMEOUT)
    ]) as Response;
    if (!response.ok) throw new Error(`Failed to fetch watch: ${response.statusText}`);
    return await response.json();
}

async function saveWatch(variantId: string, data: WatchVariant): Promise<boolean> {
    if (!ENABLE_API) {
        console.warn('API disabled, watch not saved');
        return false;
    }
    const response = await Promise.race([
        fetch(`${API_BASE_URL}/watches/${variantId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        }),
        timeoutPromise(REQUEST_TIMEOUT)
    ]) as Response;
    if (!response.ok) throw new Error(`Failed to save watch: ${response.statusText}`);
    const json = await response.json();
    return json.success === true;
}

// EditableField moved to module scope to prevent focus loss during re-renders
// Define props type for EditableField
type EditableFieldProps = {
    label?: string;
    fieldKey: string;
    value: any;
    isTextarea?: boolean;
    options?: { value: string; label: string }[];
    showLabel?: boolean;
};
const EditableField: React.FC<EditableFieldProps> = ({ label, fieldKey, value, isTextarea = false, options, showLabel }) => {
    const { isEditMode, getEditedValue, updateEditedContent } = useEditMode();
    const defaultValue = value !== undefined && value !== null ? value.toString() : '';
    // Manage local input state to avoid focus loss on context updates
    const initialValue = getEditedValue(fieldKey, defaultValue);
    const [localValue, setLocalValue] = useState<string>(initialValue);
    useEffect(() => {
        setLocalValue(getEditedValue(fieldKey, defaultValue));
    }, [fieldKey, defaultValue, isEditMode, getEditedValue]);
    return (
        <div>
            {showLabel && label && (
                <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
            )}
            {isEditMode ? (
                options ? (
                    <select
                        className="w-full p-1 border border-gray-300 rounded text-gray-600 text-sm sm:text-sm"
                        value={localValue}
                        onChange={(e) => {
                            const v = e.target.value;
                            setLocalValue(v);
                            updateEditedContent(fieldKey, v);
                        }}
                    >
                        {options.map((opt: { value: string; label: string }) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                ) : isTextarea ? (
                    <textarea
                        className="w-full p-1 border border-gray-300 rounded min-h-[60px] text-gray-600 text-sm sm:text-sm"
                        value={localValue}
                        onChange={(e) => {
                            const v = e.target.value;
                            setLocalValue(v);
                            updateEditedContent(fieldKey, v);
                        }}
                    />
                ) : (
                    <input
                        className="w-full p-1 border border-gray-300 rounded text-gray-600 text-sm sm:text-sm"
                        type="text"
                        value={localValue}
                        onChange={(e) => {
                            const v = e.target.value;
                            setLocalValue(v);
                            updateEditedContent(fieldKey, v);
                        }}
                    />
                )
            ) : (
                <p className="text-gray-600 text-sm sm:text-sm">{value}</p>
            )}
        </div>
    );
};

function VariantDetail() {
    const { collectionId, variantId } = useParams();
    const { language } = useLanguage();
    const { region, regionInfo, formatPrice } = useRegion();
    const t = getVariantDetailTranslations(language);
    const [activeTab, setActiveTab] = useState(t.case);
    const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
    const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
    const [isAppointmentPopupOpen, setIsAppointmentPopupOpen] = useState(false);
    const [variant, setVariant] = useState<WatchVariant | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { isEditMode, getEditedValue, updateEditedContent } = useEditMode();
    // State for saving variant edits
    const [isSavingVariant, setIsSavingVariant] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [saveSuccess, setSaveSuccess] = useState(false);
    
    // Regional pricing state
    const [automaticPricing, setAutomaticPricing] = useState<PricingResponse | null>(null);
    const [quartzPricing, setQuartzPricing] = useState<PricingResponse | null>(null);
    const [pricingLoading, setPricingLoading] = useState(false);
    const [pricingError, setPricingError] = useState<string | null>(null);

    // Load variant via API
    useEffect(() => {
        if (!variantId) return;
        setLoading(true);
        setError(null);
        fetchWatch(variantId)
            .then((data) => {
                setVariant(data);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setError(t.variantNotFound);
                setLoading(false);
            });
    }, [variantId, t.variantNotFound]);

    // Fetch regional pricing when variant or region changes
    useEffect(() => {
        if (!variant || !variantId) return;
        
        const fetchRegionalPricing = async () => {
            setPricingLoading(true);
            setPricingError(null);
            
            try {
                const promises: Promise<PricingResponse | null>[] = [];
                
                // Only fetch pricing for available movement types
                if (PricingService.isPriceAvailable(variant.prices?.automatic_movement)) {
                    promises.push(
                        PricingService.getRegionalPricing(variantId, region, 'automatic_movement')
                            .catch(err => {
                                console.error('Failed to fetch automatic movement pricing:', err);
                                return null;
                            })
                    );
                } else {
                    promises.push(Promise.resolve(null));
                }
                
                if (PricingService.isPriceAvailable(variant.prices?.quartz_movement)) {
                    promises.push(
                        PricingService.getRegionalPricing(variantId, region, 'quartz_movement')
                            .catch(err => {
                                console.error('Failed to fetch quartz movement pricing:', err);
                                return null;
                            })
                    );
                } else {
                    promises.push(Promise.resolve(null));
                }
                
                const [automaticResult, quartzResult] = await Promise.all(promises);
                
                setAutomaticPricing(automaticResult);
                setQuartzPricing(quartzResult);
                
            } catch (error) {
                console.error('Error fetching regional pricing:', error);
                setPricingError('Failed to load regional pricing');
            } finally {
                setPricingLoading(false);
            }
        };
        
        fetchRegionalPricing();
    }, [variant, variantId, region]);

    // Helper function to render pricing with regional conversion or fallback
    const renderPrice = (
        movementType: 'automatic_movement' | 'quartz_movement',
        originalPrice: number | string | undefined,
        regionalPricing: PricingResponse | null
    ): React.ReactNode => {
        // Show loading state while pricing is being fetched
        if (pricingLoading && PricingService.isPriceAvailable(originalPrice)) {
            return (
                <span className="text-xl font-light text-gray-400 italic">
                    Loading...
                </span>
            );
        }
        
        // Use regional pricing if available
        if (regionalPricing && !pricingError) {
            return (
                <span className="text-xl font-light text-black">
                    {formatPrice(regionalPricing.regionalPricing.price)}
                </span>
            );
        }
        
        // Fallback to original Euro pricing display
        if (typeof originalPrice === 'number') {
            return (
                <span className="text-xl font-light text-black">
                    {PricingService.formatFallbackPrice(originalPrice, regionInfo.symbol)}
                </span>
            );
        }
        
        // Try to extract numeric value from string price
        if (typeof originalPrice === 'string') {
            const extractedPrice = PricingService.extractEuroPrice(originalPrice);
            if (extractedPrice > 0) {
                return (
                    <span className="text-xl font-light text-black">
                        {PricingService.formatFallbackPrice(extractedPrice, regionInfo.symbol)}
                    </span>
                );
            }
        }
        
        // Show "On request" for invalid or unparseable prices
        return (
            <span className="text-xl font-light text-black">
                {t.priceOnRequest}
            </span>
        );
    };

    // Get variant images from the mapping
    const variantImages: VariantImage[] = [];
    if (variant) {
        const variantImageData = (variantImagesData as VariantImagesMap)[variant.code];
        if (variantImageData) {
            if (variantImageData.front) {
                variantImages.push({
                    src: variantImageData.front,
                    view: t.frontView || 'Front View'
                });
            }
            if (variantImageData.angle) {
                variantImages.push({
                    src: variantImageData.angle,
                    view: t.angleView || 'Angle View'
                });
            }
            if (variantImageData.side) {
                variantImages.push({
                    src: variantImageData.side,
                    view: t.sideView || 'Side View'
                });
            }
            if (variantImageData.back) {
                variantImages.push({
                    src: variantImageData.back,
                    view: t.backView || 'Back View'
                });
            }
        }
    }

    const handleImageLoad = (src: string) => {
        setLoadedImages(prev => new Set(Array.from(prev).concat(src)));
    };

    const handleImageError = (src: string) => {
        setFailedImages(prev => new Set(Array.from(prev).concat(src)));
    };

    // Handler to save edited variant data
    const handleSaveVariant = async () => {
        if (!variant || !variantId) return;
        setIsSavingVariant(true);
        setSaveError('');
        setSaveSuccess(false);
        try {
            const updated: WatchVariant = {
                ...variant,
                model: getEditedValue('model', variant.model),
                gender: getEditedValue('gender', variant.gender) as 'gents' | 'ladies',
                code: getEditedValue('code', variant.code),
                serialNumber: getEditedValue('serialNumber', variant.serialNumber || ''),
                description: { ...variant.description, [language]: getEditedValue('description', variant.description[language]) },
                case: { ...variant.case, [language]: getEditedValue('case', variant.case[language]) },
                movement: { ...variant.movement, [language]: getEditedValue('movement', variant.movement[language]) },
                dial: { ...variant.dial, [language]: getEditedValue('dial', variant.dial[language]) },
                indexGlass: { ...variant.indexGlass, [language]: getEditedValue('indexGlass', variant.indexGlass[language]) },
                bracelet: { ...variant.bracelet, [language]: getEditedValue('bracelet', variant.bracelet[language]) },
                prices: {
                    automatic_movement: getEditedValue('prices.automatic_movement', variant.prices?.automatic_movement?.toString() || ''),
                    quartz_movement: getEditedValue('prices.quartz_movement', variant.prices?.quartz_movement?.toString() || '')
                }
            };
            const success = await saveWatch(variantId, updated);
            if (!success) throw new Error('Save failed');
            setVariant(updated);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (err) {
            console.error(err);
            setSaveError('Failed to save variant');
        } finally {
            setIsSavingVariant(false);
        }
    };

    // Register a global save handler for the FloatingSaveButton to call on the variant page
    useEffect(() => {
        if (isEditMode) {
            (window as any).customSaveVariant = handleSaveVariant;
        } else {
            delete (window as any).customSaveVariant;
        }
        return () => {
            delete (window as any).customSaveVariant;
        };
    }, [isEditMode, handleSaveVariant]);

    if (loading) {
        return <div>Loading...</div>;
    }
    if (error || !variant) {
        return <div>{error || t.variantNotFound}</div>;
    }

    const carouselResponsive = {
        desktop: {
            breakpoint: { max: 3000, min: 1024 },
            items: 1
        },
        tablet: {
            breakpoint: { max: 1024, min: 464 },
            items: 1
        },
        mobile: {
            breakpoint: { max: 464, min: 0 },
            items: 1
        }
    };

    const tabs = [t.case, t.dial, t.indexGlass, t.bracelet];

    const renderTabContent = () => {
        switch (activeTab) {
            case t.case:
                return (
                    <div className="space-y-4">
                        <div>
                            <EditableField label={t.material} fieldKey="case" value={variant.case[language]} showLabel />
                        </div>
                        <div>
                            <EditableField label={t.movement} fieldKey="movement" value={variant.movement[language]} showLabel />
                        </div>
                    </div>
                );
            case t.dial:
                return <EditableField fieldKey="dial" value={variant.dial[language]} isTextarea />;
            case t.indexGlass:
                return <EditableField fieldKey="indexGlass" value={variant.indexGlass[language]} isTextarea />;
            case t.bracelet:
                return <EditableField fieldKey="bracelet" value={variant.bracelet[language]} isTextarea />;
            default:
                return null;
        }
    };

    const renderImages = () => {

        // If we have carousel images, show them in a carousel.
        if (variant?.carousel_images && variant.carousel_images.length > 0) {
            return (
                <Carousel
                    responsive={carouselResponsive}
                    infinite={true}
                    showDots={false}
                    removeArrowOnDeviceType={["tablet", "mobile"]}
                    className="product-carousel"
                >
                    {variant.carousel_images
                        .filter(imageSrc => !failedImages.has(imageSrc)) // Use the full image src for failedImages check
                        .map((imageName, index) => (
                            <div key={index} className="relative aspect-square">
                                <img
                                    src={`/images/watch_variants_wa/${imageName}.png`}
                                    alt={`${variant.model} - view ${index + 1}`}
                                    className="w-full h-full object-contain"
                                    onLoad={() => handleImageLoad(`/images/watch_variants/${imageName}`)} // Pass full src to handler
                                    onError={() => handleImageError(`/images/watch_variants/${imageName}`)} // Pass full src to handler
                                />
                            </div>
                        ))}
                </Carousel>
            );
        }

        // Otherwise, display the single image as it is currently.
        return (
            <div className="relative aspect-square">
                <img
                    src={`/images/${variant.imageSrc}.png`}
                    alt={`${variant.model}`}
                    className="w-full h-full object-contain"
                    onLoad={() => handleImageLoad(variant.imageSrc)} // Keep existing logic for single image
                    onError={() => handleImageError(variant.imageSrc)} // Keep existing logic for single image
                />
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-white">
            <AppointmentPopup
                isOpen={isAppointmentPopupOpen}
                onClose={() => setIsAppointmentPopupOpen(false)}
            />
            <div className="mx-auto px-4 sm:px-6 lg:px-24 mt-[80px] sm:mt-[100px] lg:mt-[150px]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12">
                    {/* Left Column - Details */}
                    <div className={`${isEditMode ? 'space-y-2 lg:space-y-4' : 'space-y-6 lg:space-y-8'} order-2 lg:order-1`}>
                        <Title
                            firstLine={collectionId?.toUpperCase()}
                            secondLine={variant.gender}
                            className='mb-4'
                        />

                        {/* Model editing field only in edit mode */}
                        {isEditMode && (
                            <EditableField label="Model" fieldKey="model" value={variant.model} showLabel={true} />
                        )}

                        {/* Gender editing field only in edit mode */}
                        {isEditMode && (
                            <EditableField label="Gender" fieldKey="gender" value={variant.gender} showLabel={true} />
                        )}

                        {/* Code editing field only in edit mode */}
                        {isEditMode && (
                            <EditableField label="Code" fieldKey="code" value={variant.code} showLabel={true} />
                        )}

                        {/* Description: manual render in view, editable field in edit mode */}
                        {isEditMode ? (
                            <EditableField label="Description" fieldKey="description" value={variant.description[language]} isTextarea={true} showLabel={true} />
                        ) : (
                            <p className="text-gray-600 text-sm sm:text-sm">{variant.description[language]}</p>
                        )}

                        {variant.prices || isEditMode ? (
                            <div className={`${isEditMode ? 'my-2 py-2' : 'my-6 py-4'} border-t border-b border-gray-200 flex flex-col sm:flex-row sm:justify-start sm:items-center sm:gap-8`}>
                                {variant.prices?.automatic_movement || isEditMode ? (
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-xs uppercase tracking-wider text-gray-500">{t.automaticMovement}</span>
                                        {isEditMode ? (
                                            <input
                                                className="p-1 border border-gray-300 rounded text-black font-light text-xl w-24"
                                                value={getEditedValue('prices.automatic_movement', (variant.prices?.automatic_movement ?? '').toString())}
                                                onChange={(e) => updateEditedContent('prices.automatic_movement', e.target.value)}
                                                type="text" // Use text to allow "on request"
                                            />
                                        ) : (
                                            renderPrice('automatic_movement', variant.prices?.automatic_movement, automaticPricing)
                                        )}
                                    </div>
                                ) : null}
                                {variant.prices?.quartz_movement || isEditMode ? (
                                    <div className="flex items-baseline gap-2 mt-2 sm:mt-0">
                                        <span className="text-xs uppercase tracking-wider text-gray-500">{t.quartzMovement}</span>
                                        {isEditMode ? (
                                            <input
                                                className="p-1 border border-gray-300 rounded text-black font-light text-xl w-24"
                                                value={getEditedValue('prices.quartz_movement', (variant.prices?.quartz_movement ?? '').toString())}
                                                onChange={(e) => updateEditedContent('prices.quartz_movement', e.target.value)}
                                                type="text" // Use text to allow "on request"
                                            />
                                        ) : (
                                            renderPrice('quartz_movement', variant.prices?.quartz_movement, quartzPricing)
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}

                        <div>
                            <div className="flex border-b border-gray-200 overflow-x-auto no-scrollbar">
                                {tabs.filter(tab => tab !== 'DESCRIPTION').map(tab => (
                                    <Tab
                                        key={tab}
                                        label={tab}
                                        isActive={activeTab === tab}
                                        onClick={() => setActiveTab(tab)}
                                    />
                                ))}
                            </div>
                            <div className={`${isEditMode ? 'mt-2 lg:mt-4' : 'mt-4 lg:mt-6'} h-auto lg:h-[130px] overflow-y-auto custom-scrollbar`}>
                                {renderTabContent()}
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Image */}
                    <div className="lg:pl-12 order-1 lg:order-2">
                        {renderImages()}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VariantDetail; 